// src/modules/system/ErrorLogDetail.js
//
// Everything known about one error. This is the page you actually diagnose on,
// so it favors density over polish: the stack, the breadcrumb timeline (what
// the user did in the seconds before the crash) and the device/version spread
// are all visible without clicking.
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  FaArrowLeft, FaCheck, FaBan, FaSearch, FaTrash, FaCopy, FaRedo,
  FaChevronLeft, FaChevronRight, FaCode,
} from 'react-icons/fa';
import DashboardLayout from '../../layouts/DashboardLayout';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import Modal from '../../components/shared/Modal';
import FeedbackModal from '../../components/shared/ModalNotification';
import { StatusBadge, PageHeader, HeaderContent } from '../../components/styled/CommonStyles';
import {
  getError, setErrorStatus, deleteError, listErrors, getRelated,
  severityColor, statusColor, statusLabel, sourceLabel, relativeTime,
} from './errorLogService';

// Query keys the list page appends for review context — not ErrorLog filters.
const CTX_KEYS = ['_page', '_per'];

const Panel = styled.div`
  background: ${(p) => p.theme.colors.surface}; border-radius: ${(p) => p.theme.borderRadius};
  padding: ${(p) => p.theme.spacing(3)}; margin-bottom: ${(p) => p.theme.spacing(3)};
  box-shadow: 0 2px 4px rgba(0,0,0,.1);
  h3 { margin: 0 0 ${(p) => p.theme.spacing(2)}; font-size: .95rem; font-weight: 600;
       color: ${(p) => p.theme.colors.textPrimary};
       display: flex; align-items: center; justify-content: space-between; gap: 8px; }
  @media (max-width: 900px) {
    padding: ${(p) => p.theme.spacing(2)};
    margin-bottom: ${(p) => p.theme.spacing(2)};
  }
`;
const Title = styled.div`
  h2 { margin: 0 0 6px; font-size: 1.15rem; color: ${(p) => p.theme.colors.textPrimary};
       word-break: break-word; }
  .type { color: ${(p) => p.theme.colors.textSecondary}; font-size: .8rem;
          word-break: break-word; }
  @media (max-width: 900px) {
    h2 { font-size: 1rem; line-height: 1.35; }
    .type { font-size: .72rem; }
  }
`;
const Badges = styled.div`display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: ${(p) => p.theme.spacing(2)};`;
const Actions = styled.div`
  display: flex; gap: 8px; flex-wrap: wrap;
  button { display: flex; align-items: center; gap: 6px; cursor: pointer;
    background: transparent; border: 1px solid ${(p) => p.theme.colors.inputGray};
    color: ${(p) => p.theme.colors.textSecondary}; border-radius: ${(p) => p.theme.borderRadius};
    padding: 7px 13px; font-size: .85rem; }
  button:hover { color: ${(p) => p.theme.colors.primary}; border-color: ${(p) => p.theme.colors.primary}; }
  button.danger:hover { color: ${(p) => p.theme.colors.error}; border-color: ${(p) => p.theme.colors.error}; }
  @media (max-width: 900px) {
    gap: 6px;
    /* Two per row, full-width taps rather than a ragged wrap of small pills. */
    button { flex: 1 1 calc(50% - 3px); justify-content: center; padding: 10px 8px; }
  }
`;
const Grid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: ${(p) => p.theme.spacing(2)};
  @media (max-width: 900px) {
    grid-template-columns: 1fr 1fr;
    gap: ${(p) => p.theme.spacing(1.5)};
  }
`;
const Item = styled.div`
  .k { color: ${(p) => p.theme.colors.textSecondary}; font-size: .72rem; text-transform: uppercase;
       letter-spacing: .04em; }
  .v { color: ${(p) => p.theme.colors.textPrimary}; font-size: .9rem; margin-top: 3px;
       word-break: break-word; }
  .v.mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: .78rem; }
`;
const Pre = styled.pre`
  background: ${(p) => p.theme.colors.background}; color: ${(p) => p.theme.colors.textPrimary};
  border: 1px solid ${(p) => p.theme.colors.inputGray}; border-radius: ${(p) => p.theme.borderRadius};
  padding: ${(p) => p.theme.spacing(2)}; overflow-x: auto; margin: 0;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: .76rem;
  line-height: 1.55; white-space: pre; max-height: 460px;

  /* The horizontal scroll stays INSIDE this box (overflow-x above), so the page
     itself never scrolls sideways. On a phone, wrap long frames instead —
     swiping a stack trace one frame at a time is worse than a ragged wrap. */
  @media (max-width: 900px) {
    font-size: .68rem; line-height: 1.5; padding: ${(p) => p.theme.spacing(1.5)};
    white-space: pre-wrap; word-break: break-word; max-height: 320px;
  }
`;
const CopyBtn = styled.button`
  background: transparent; border: 1px solid ${(p) => p.theme.colors.inputGray};
  color: ${(p) => p.theme.colors.textSecondary}; border-radius: ${(p) => p.theme.borderRadius};
  padding: 4px 9px; cursor: pointer; font-size: .72rem;
  display: inline-flex; align-items: center; gap: 5px; font-weight: 400;
  &:hover { color: ${(p) => p.theme.colors.primary}; }
`;
const Crumbs = styled.ol`
  list-style: none; margin: 0; padding: 0;
  li { display: flex; gap: 10px; padding: 7px 0;
       border-bottom: 1px solid ${(p) => p.theme.colors.inputGray}44; }
  li:last-child { border-bottom: none; }
  .tag { flex: 0 0 74px; font-size: .68rem; text-transform: uppercase; letter-spacing: .04em;
         color: ${(p) => p.theme.colors.textSecondary}; padding-top: 2px; }
  .body { flex: 1; min-width: 0; }
  .msg { color: ${(p) => p.theme.colors.textPrimary}; font-size: .84rem; word-break: break-word; }
  .data { color: ${(p) => p.theme.colors.textSecondary}; font-size: .72rem; margin-top: 2px;
          font-family: ui-monospace, SFMono-Regular, Menlo, monospace; word-break: break-all; }
  .at { flex: 0 0 auto; color: ${(p) => p.theme.colors.textSecondary}; font-size: .7rem; padding-top: 2px; }

  /* A 74px label gutter plus a timestamp column leaves ~180px for the message
     on a phone. Stack instead: type + time on one line, message below. */
  @media (max-width: 900px) {
    li { flex-wrap: wrap; gap: 4px 8px; }
    .tag { flex: 0 0 auto; font-size: .62rem; order: 1; }
    .at { order: 2; margin-left: auto; }
    .body { order: 3; flex: 1 1 100%; }
    .msg { font-size: .8rem; }
  }
`;
const Spark = styled.div`
  display: flex; align-items: flex-end; gap: 3px; height: 54px; margin-top: 6px;
  .bar { flex: 1; background: ${(p) => p.theme.colors.primary}; border-radius: 2px 2px 0 0; min-height: 2px; }
  .bar.zero { background: ${(p) => p.theme.colors.inputGray}; }
`;
const NotesArea = styled.textarea`
  width: 100%; min-height: 84px; resize: vertical;
  background: ${(p) => p.theme.colors.background}; color: ${(p) => p.theme.colors.textPrimary};
  border: 1px solid ${(p) => p.theme.colors.inputGray}; border-radius: ${(p) => p.theme.borderRadius};
  padding: ${(p) => p.theme.spacing(2)}; font-family: inherit; font-size: .88rem;
`;
const SaveBtn = styled.button`
  margin-top: 10px; background: ${(p) => p.theme.colors.primary}; color: ${(p) => p.theme.colors.black};
  border: none; border-radius: ${(p) => p.theme.borderRadius}; cursor: pointer;
  padding: 8px 16px; font-weight: 500;
`;
const BackBtn = styled.button`
  background: transparent; border: none; cursor: pointer; padding: 0;
  color: ${(p) => p.theme.colors.textSecondary}; display: flex; align-items: center; gap: 7px;
  font-size: .88rem;
  &:hover { color: ${(p) => p.theme.colors.primary}; }
`;
const TopBar = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  flex-wrap: wrap; margin-bottom: ${(p) => p.theme.spacing(2)};
`;
const Stepper = styled.div`
  display: flex; align-items: center; gap: 8px;
  .pos { color: ${(p) => p.theme.colors.textSecondary}; font-size: .8rem;
         font-variant-numeric: tabular-nums; }
  .hint { color: ${(p) => p.theme.colors.textSecondary}; font-size: .68rem; opacity: .65; }
  button { display: flex; align-items: center; gap: 5px; cursor: pointer;
    background: transparent; border: 1px solid ${(p) => p.theme.colors.inputGray};
    color: ${(p) => p.theme.colors.textSecondary}; border-radius: ${(p) => p.theme.borderRadius};
    padding: 6px 11px; font-size: .82rem; }
  button:hover:not(:disabled) { color: ${(p) => p.theme.colors.primary};
    border-color: ${(p) => p.theme.colors.primary}; }
  button:disabled { opacity: .35; cursor: default; }

  @media (max-width: 900px) {
    width: 100%; justify-content: space-between;
    /* Keyboard hint is meaningless on a touch device. */
    .hint { display: none; }
    button { flex: 1 1 0; justify-content: center; padding: 9px 10px; }
    .pos { flex: 0 0 auto; padding: 0 6px; }
  }
`;
const RelatedList = styled.ul`
  list-style: none; margin: 0; padding: 0;
  li { display: flex; gap: 10px; align-items: baseline; padding: 7px 0; cursor: pointer;
       border-bottom: 1px solid ${(p) => p.theme.colors.inputGray}44; }
  li:last-child { border-bottom: none; }
  li:hover .m { color: ${(p) => p.theme.colors.primary}; }
  .m { flex: 1; min-width: 0; color: ${(p) => p.theme.colors.textPrimary}; font-size: .84rem;
       overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .meta { flex: 0 0 auto; color: ${(p) => p.theme.colors.textSecondary}; font-size: .72rem; }
`;
const Tabs = styled.div`
  display: flex; gap: 6px; margin-bottom: ${(p) => p.theme.spacing(2)}; flex-wrap: wrap;
  button { cursor: pointer; background: transparent; font-size: .78rem;
    border: 1px solid ${(p) => p.theme.colors.inputGray}; border-radius: 20px;
    color: ${(p) => p.theme.colors.textSecondary}; padding: 4px 12px; }
  button[data-active='true'] { color: ${(p) => p.theme.colors.primary};
    border-color: ${(p) => p.theme.colors.primary}; }
`;

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

const Field = ({ k, v, mono }) => (
  <Item><div className="k">{k}</div><div className={`v${mono ? ' mono' : ''}`}>{v || '—'}</div></Item>
);

const ErrorLogDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackMsg, setFeedbackMsg] = useState('');
  const [feedbackSuccess, setFeedbackSuccess] = useState(true);
  const [siblings, setSiblings] = useState([]);      // ids of the filtered view
  const [relatedDim, setRelatedDim] = useState('device');
  const [related, setRelated] = useState([]);

  // The filter context the list page handed us, minus its own bookkeeping keys.
  const ctxQuery = searchParams.toString();
  const listFilters = React.useMemo(() => {
    const out = {};
    searchParams.forEach((v, k) => { if (!CTX_KEYS.includes(k) && v) out[k] = v; });
    return out;
  }, [searchParams]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getError(id);
      setRow(data);
      setNotes(data.notes || '');
    } catch (e) {
      setRow(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Fetch the ids of the same filtered view so Prev/Next steps through exactly
  // what you were looking at. Capped at 200 — past that, stepping is hidden
  // rather than silently wrong.
  const filterKey = JSON.stringify(listFilters);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const applied = JSON.parse(filterKey);
        const data = await listErrors({
          ...applied,
          to: applied.to ? `${applied.to}T23:59:59` : '',
          limit: 200,
          offset: 0,
        });
        if (!cancelled) setSiblings((data.errors || []).map((e) => e.id));
      } catch (e) {
        if (!cancelled) setSiblings([]);
      }
    })();
    return () => { cancelled = true; };
  }, [filterKey]);

  const numericId = Number(id);
  const idx = siblings.indexOf(numericId);
  const prevId = idx > 0 ? siblings[idx - 1] : null;
  const nextId = idx >= 0 && idx < siblings.length - 1 ? siblings[idx + 1] : null;

  const goTo = useCallback((targetId) => {
    if (!targetId) return;
    navigate(`/system/errors/${targetId}${ctxQuery ? `?${ctxQuery}` : ''}`);
  }, [navigate, ctxQuery]);

  // Keyboard stepping. Ignored while typing in the notes field.
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'ArrowLeft' || e.key === 'k') { e.preventDefault(); goTo(prevId); }
      if (e.key === 'ArrowRight' || e.key === 'j') { e.preventDefault(); goTo(nextId); }
      if (e.key === 'Escape') navigate(`/system/errors${ctxQuery ? `?${ctxQuery}` : ''}`);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goTo, prevId, nextId, navigate, ctxQuery]);

  // Which correlation dimensions this row can actually offer, in priority order.
  const availableDims = React.useMemo(() => {
    if (!row) return [];
    return [
      { k: 'device', label: 'Same device', on: !!row.device_id },
      { k: 'session', label: 'Same session', on: !!row.session_id },
      { k: 'user', label: 'Same user', on: !!row.user_id },
      { k: 'type', label: `Same type${row.error_type ? ` (${row.error_type})` : ''}`, on: !!row.error_type },
    ].filter((d) => d.on);
  }, [row]);

  // A backend error has no device_id, so the default tab must fall back to
  // whatever this row can actually correlate on, or the panel reads as empty.
  const activeDim = availableDims.some((d) => d.k === relatedDim)
    ? relatedDim
    : (availableDims[0] && availableDims[0].k);

  useEffect(() => {
    if (!row || !activeDim) { setRelated([]); return undefined; }
    let cancelled = false;
    getRelated(row, activeDim).then((r) => { if (!cancelled) setRelated(r); });
    return () => { cancelled = true; };
  }, [row, activeDim]);

  const feedback = (msg, ok = true) => {
    setFeedbackMsg(msg); setFeedbackSuccess(ok); setShowFeedback(true);
  };

  const changeStatus = async (status) => {
    try {
      const updated = await setErrorStatus(id, status);
      setRow(updated);
      feedback(`Marked ${statusLabel(status).toLowerCase()}`);
    } catch (e) {
      feedback(e.response?.data?.message || 'Failed to update status', false);
    }
  };
  const saveNotes = async () => {
    try {
      const updated = await setErrorStatus(id, undefined, notes);
      setRow(updated);
      feedback('Notes saved');
    } catch (e) {
      feedback(e.response?.data?.message || 'Failed to save notes', false);
    }
  };
  const doDelete = async () => {
    setConfirmDelete(false);
    try {
      await deleteError(id);
      navigate('/system/errors');
    } catch (e) {
      feedback(e.response?.data?.message || 'Failed to delete', false);
    }
  };
  const copy = (text) => {
    if (!text) return;
    navigator.clipboard?.writeText(text)
      .then(() => feedback('Copied to clipboard'))
      .catch(() => feedback('Could not copy', false));
  };

  const backToList = () => navigate(`/system/errors${ctxQuery ? `?${ctxQuery}` : ''}`);

  // The whole record as JSON — for pasting into a ticket or an AI agent.
  const copyJson = () => {
    if (!row) return;
    copy(JSON.stringify(row, null, 2));
  };

  if (loading) return <DashboardLayout><LoadingSpinner /></DashboardLayout>;
  if (!row) {
    return (
      <DashboardLayout>
        <BackBtn onClick={backToList}><FaArrowLeft /> Back to System Errors</BackBtn>
        <Panel><h3>Error log not found</h3></Panel>
      </DashboardLayout>
    );
  }

  const crumbs = Array.isArray(row.breadcrumbs) ? [...row.breadcrumbs].reverse() : [];
  const daily = row.daily_counts || {};
  const dailyKeys = Object.keys(daily).sort();
  const dailyMax = Math.max(1, ...dailyKeys.map((k) => Number(daily[k]) || 0));
  const affected = Array.isArray(row.affected_users) ? row.affected_users : [];
  const isMobile = row.source === 'mobile';

  return (
    <DashboardLayout>
      <TopBar>
        <BackBtn onClick={backToList}><FaArrowLeft /> Back to System Errors</BackBtn>
        {idx >= 0 && siblings.length > 1 && (
          <Stepper>
            <button onClick={() => goTo(prevId)} disabled={!prevId} title="Previous (← or K)">
              <FaChevronLeft /> Prev
            </button>
            <span className="pos">{idx + 1} of {siblings.length}</span>
            <button onClick={() => goTo(nextId)} disabled={!nextId} title="Next (→ or J)">
              Next <FaChevronRight />
            </button>
            <span className="hint">← → to step · Esc for list</span>
          </Stepper>
        )}
      </TopBar>

      <PageHeader>
        <HeaderContent>
          <Title>
            <Badges>
              <StatusBadge color={severityColor(row.severity)}>
                {row.is_fatal ? 'crash' : row.severity}
              </StatusBadge>
              <StatusBadge color={statusColor(row.status)}>{statusLabel(row.status)}</StatusBadge>
              <StatusBadge color="#7f8c8d">
                {sourceLabel(row.source)}{row.platform ? ` · ${row.platform}` : ''}
              </StatusBadge>
            </Badges>
            <h2>{row.message}</h2>
            <div className="type">
              {row.error_type || 'Error'}
              {row.route ? ` · ${row.route}` : ''}
              {row.status_code ? ` · HTTP ${row.status_code}` : ''}
            </div>
          </Title>
        </HeaderContent>
      </PageHeader>

      <Panel>
        <Actions>
          {row.status !== 'resolved'
            ? <button onClick={() => changeStatus('resolved')}><FaCheck /> Resolve</button>
            : <button onClick={() => changeStatus('new')}><FaRedo /> Reopen</button>}
          {row.status !== 'investigating' &&
            <button onClick={() => changeStatus('investigating')}><FaSearch /> Investigating</button>}
          {row.status !== 'ignored' &&
            <button onClick={() => changeStatus('ignored')}><FaBan /> Ignore</button>}
          <button onClick={copyJson} title="Copy the full record as JSON"><FaCode /> Copy JSON</button>
          <button className="danger" onClick={() => setConfirmDelete(true)}><FaTrash /> Delete</button>
        </Actions>
      </Panel>

      <Panel>
        <h3>Summary</h3>
        <Grid>
          <Field k="Occurrences" v={row.occurrences} />
          <Field k="Affected users" v={affected.length} />
          <Field k="First seen" v={fmt(row.first_seen_at)} />
          <Field k="Last seen" v={`${fmt(row.last_seen_at)} (${relativeTime(row.last_seen_at)})`} />
          {row.reopened_at && <Field k="Reopened" v={fmt(row.reopened_at)} />}
          {row.resolved_at && <Field k="Resolved" v={fmt(row.resolved_at)} />}
          {row.resolver && <Field k="Resolved by" v={row.resolver.username} />}
          {isMobile && <Field k="App version" v={row.build_number ? `${row.app_version || '—'} (${row.build_number})` : row.app_version} />}
          {isMobile && <Field k="OS" v={row.os_version ? `${row.os_name || ''} ${row.os_version}`.trim() : null} />}
          {isMobile && <Field k="Device" v={row.device_model} />}
          {isMobile && <Field k="Release stage" v={row.release_stage} />}
          {isMobile && <Field k="Device ID" v={row.device_id} mono />}
          {isMobile && <Field k="Session" v={row.session_id} mono />}
          <Field k="Fingerprint" v={row.fingerprint} mono />
        </Grid>
      </Panel>

      {(row.url || row.http_method || row.ip || row.user) && (
        <Panel>
          <h3>Request context</h3>
          <Grid>
            <Field k="Method" v={row.http_method} />
            <Field k="URL" v={row.url} mono />
            <Field k="Route" v={row.route} mono />
            <Field k="Status code" v={row.status_code} />
            <Field k="IP" v={row.ip} mono />
            <Field
              k="User"
              v={row.user ? `${row.user.username} (${row.user.email}) · ${row.user.role}`
                : (row.user_email || (row.user_id ? `#${row.user_id}` : null))}
            />
            <Field k="User agent" v={row.user_agent} />
          </Grid>
        </Panel>
      )}

      {row.stack && (
        <Panel>
          <h3>
            Stack trace
            <CopyBtn onClick={() => copy(row.stack)}><FaCopy /> Copy</CopyBtn>
          </h3>
          <Pre>{row.stack}</Pre>
        </Panel>
      )}

      {row.component_stack && (
        <Panel>
          <h3>
            Component stack
            <CopyBtn onClick={() => copy(row.component_stack)}><FaCopy /> Copy</CopyBtn>
          </h3>
          <Pre>{row.component_stack}</Pre>
        </Panel>
      )}

      {crumbs.length > 0 && (
        <Panel>
          {/* Most recent first — the last thing that happened before the crash
              is the thing you want to read first. */}
          <h3>Breadcrumbs <span style={{ fontWeight: 400, opacity: .6 }}>most recent first</span></h3>
          <Crumbs>
            {crumbs.map((c, i) => (
              <li key={i}>
                <div className="tag">{c.type || 'log'}</div>
                <div className="body">
                  <div className="msg">{c.message}</div>
                  {c.data && Object.keys(c.data).length > 0 &&
                    <div className="data">{JSON.stringify(c.data)}</div>}
                </div>
                <div className="at">{c.t ? new Date(c.t).toLocaleTimeString() : ''}</div>
              </li>
            ))}
          </Crumbs>
        </Panel>
      )}

      {dailyKeys.length > 0 && (
        <Panel>
          <h3>Occurrences per day</h3>
          <Spark>
            {dailyKeys.map((k) => {
              const n = Number(daily[k]) || 0;
              return (
                <div key={k} className={`bar${n === 0 ? ' zero' : ''}`}
                  style={{ height: `${Math.max(4, (n / dailyMax) * 100)}%` }}
                  title={`${k}: ${n}`} />
              );
            })}
          </Spark>
          <div style={{ fontSize: '.7rem', opacity: .6, marginTop: 6 }}>
            {dailyKeys[0]} → {dailyKeys[dailyKeys.length - 1]} · peak {dailyMax}/day
          </div>
        </Panel>
      )}

      {row.context && Object.keys(row.context).length > 0 && (
        <Panel>
          <h3>
            Context
            <CopyBtn onClick={() => copy(JSON.stringify(row.context, null, 2))}><FaCopy /> Copy</CopyBtn>
          </h3>
          <Pre>{JSON.stringify(row.context, null, 2)}</Pre>
        </Panel>
      )}

      {/* Chasing a crash is usually about finding the cluster, not the single
          row: everything from one phone, one app session, one user, one class. */}
      <Panel>
        <h3>Related errors</h3>
        <Tabs>
          {availableDims.map((t) => (
            <button key={t.k} data-active={activeDim === t.k} onClick={() => setRelatedDim(t.k)}>
              {t.label}
            </button>
          ))}
        </Tabs>
        {related.length === 0 ? (
          <div style={{ opacity: .6, fontSize: '.85rem' }}>
            {availableDims.length === 0
              ? 'This error carries no device, session or user to correlate on.'
              : 'Nothing else matches this dimension.'}
          </div>
        ) : (
          <RelatedList>
            {related.map((r) => (
              <li key={r.id} onClick={() => goTo(r.id)}>
                <StatusBadge color={severityColor(r.severity)}>
                  {r.is_fatal ? 'crash' : r.severity}
                </StatusBadge>
                <span className="m" title={r.message}>{r.message}</span>
                <span className="meta">{r.occurrences}× · {relativeTime(r.last_seen_at)}</span>
              </li>
            ))}
          </RelatedList>
        )}
      </Panel>

      <Panel>
        <h3>Notes</h3>
        <NotesArea value={notes} onChange={(e) => setNotes(e.target.value)}
          placeholder="What did you find? What fixed it?" />
        <SaveBtn onClick={saveNotes}>Save notes</SaveBtn>
      </Panel>

      {confirmDelete && (
        <Modal
          isOpen={confirmDelete}
          title="Delete Error Log"
          message="Permanently delete this error log? This cannot be undone, and it will not stop the error from recurring — if you want it out of your way, use Ignore instead."
          onConfirm={doDelete}
          onCancel={() => setConfirmDelete(false)}
        />
      )}

      <FeedbackModal isOpen={showFeedback} message={feedbackMsg}
        success={feedbackSuccess} onComplete={() => setShowFeedback(false)} />
    </DashboardLayout>
  );
};

export default ErrorLogDetail;
